import { useEffect, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import type {
  PrepareExposureForPaymentResponse,
  UpdateExposureLineItemsRequestExposureLineItemData,
} from '~/server_shared/openapi/data-contracts';

import { useClaim } from '../../ClaimContainer';
import type {
  ExposureLineItemsWithSummary,
  Invoice,
  InvoiceCoveragesBreakdown,
  LineItemWithInvoiceInfo,
  SettlementParams,
  SummaryItem,
} from '../../types/invoice-types';
import useDataFetcher from '../../useDataFetcher';

// Stable references to prevent infinite re-renders in consuming components
const EMPTY_LINE_ITEMS: LineItemWithInvoiceInfo[] = [];
const EMPTY_SUMMARY: SummaryItem[] = [];
const NOOP_RELOAD_DATA = () => Promise.resolve(undefined);

type UseFetchInvoicesReturn = {
  isInvoicesLoading: boolean;
  isInvoicesError: boolean;
  invoices: Invoice[];
  reloadData: () => Promise<{ invoices: Invoice[] } | undefined>;
};

type UseFetchExposureLineItemsReturn = {
  isLoading: boolean;
  isError: boolean;
  lineItems: LineItemWithInvoiceInfo[];
  summaryItems: SummaryItem[];
  settlementParams: SettlementParams | null;
  reloadData: () => Promise<ExposureLineItemsWithSummary | undefined>;
};

export const useInvoices = () => {
  const useFetchInvoices = ({ coverageId }: { coverageId?: number }): UseFetchInvoicesReturn => {
    const { claim, reloadingUuid } = useClaim({ claimRequired: true });
    const [lastReloadingUuid, setLastReloadingUuid] = useState(reloadingUuid);
    const url = `/api/v1/claims/${claim.id}/invoices`;
    const config: AxiosRequestConfig = {
      params: {
        coverage_id: coverageId,
      },
    };
    const {
      isLoading: isInvoicesLoading,
      isError: isInvoicesError,
      data,
      reloadData,
    } = useDataFetcher<{ invoices: Invoice[] }>(url, config);

    useEffect(() => {
      if (!isInvoicesLoading && reloadingUuid !== lastReloadingUuid) {
        reloadData();
        setLastReloadingUuid(reloadingUuid);
      }
    }, [isInvoicesLoading, reloadData, reloadingUuid, lastReloadingUuid]);

    if (isInvoicesLoading) {
      return {
        isInvoicesLoading,
        isInvoicesError,
        invoices: [],
        reloadData: () => Promise.resolve({ invoices: [] }),
      };
    }

    const { invoices = [] } = data || {};
    return { isInvoicesLoading, isInvoicesError, invoices, reloadData };
  };

  const useFetchExposureLineItems = ({ exposureId }: { exposureId: number }): UseFetchExposureLineItemsReturn => {
    const { claim, reloadingUuid } = useClaim({ claimRequired: true });
    const lastReloadingUuidRef = useRef(reloadingUuid);
    const url = `/api/v1/claims/${claim.id}/invoice_based_da/exposure/${exposureId}/line_items`;
    const { isLoading, isError, data, reloadData } = useDataFetcher<ExposureLineItemsWithSummary>(url);

    useEffect(() => {
      if (!isLoading && reloadingUuid !== lastReloadingUuidRef.current) {
        reloadData();
        lastReloadingUuidRef.current = reloadingUuid;
      }
    }, [isLoading, reloadData, reloadingUuid]);

    const lineItems = data?.line_items ?? EMPTY_LINE_ITEMS;
    const summaryItems = data?.summary ?? EMPTY_SUMMARY;
    const settlementParams = data?.settlement_params ?? null;
    return {
      isLoading,
      isError,
      lineItems,
      summaryItems,
      settlementParams,
      reloadData: isLoading ? NOOP_RELOAD_DATA : reloadData,
    };
  };

  const getSingleInvoiceCoveragesBreakdown = async (params: {
    claimId: number;
    documentId?: number;
    claimDocumentSourceId?: string;
  }): Promise<InvoiceCoveragesBreakdown> => {
    if (!params.documentId && !params.claimDocumentSourceId) {
      throw new Error('Either documentId or claimDocumentSourceId must be provided');
    }
    const url = `/api/v1/claims/${params.claimId}/invoice_based_da/single_invoice_coverages_breakdown`;
    const config: AxiosRequestConfig = {
      params: {
        document_id: params.documentId,
        invoice_document_source_id: params.claimDocumentSourceId,
      },
    };
    const response = await axios.get(url, config);
    return response.data;
  };

  const prepareExposureForPayment = async (
    claimId: number,
    exposureId: number,
    amount: number
  ): Promise<PrepareExposureForPaymentResponse> => {
    const url = `/api/v1/claims/${claimId}/invoice_based_da/prepare_exposure_for_payment`;
    const response = await axios.post<PrepareExposureForPaymentResponse>(url, {
      exposure_id: exposureId,
      amount,
    });
    return response.data;
  };

  const updateExposureLineItems = async (
    claimId: number,
    exposureId: number,
    lineItems: UpdateExposureLineItemsRequestExposureLineItemData[]
  ): Promise<LineItemWithInvoiceInfo[]> => {
    const url = `/api/v1/claims/${claimId}/invoice_based_da/exposure/${exposureId}/line_items`;
    const response = await axios.post<LineItemWithInvoiceInfo[]>(url, {
      line_items: lineItems,
    });
    return response.data;
  };

  return {
    useFetchInvoices,
    useFetchExposureLineItems,
    getSingleInvoiceCoveragesBreakdown,
    prepareExposureForPayment,
    updateExposureLineItems,
  };
};
